import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { Box, Container, ThemeProvider } from '@material-ui/core';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useAppSuppliedRedirectTo } from '../hooks/usePostAuthRedirectTo';

import sleepioTheme from '@/themes/sleepioTheme';
import MagicLinkSent from '@/pages/Onboarding/components/MagicLinkSent/MagicLinkSent';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { activateSleepioEmail } from '@/utils/sleepioActivationApi';
import { loginUser } from '@/utils/sparkActivationApi';
import { getLob } from '@/utils/productHelpers';
import SleepioThumbsUp from '@/assets/SleepioThumbsUp.svg';

const EmailSentInner: React.FC = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useActivation();
  const isCreate = state.accountIntent === AccountIntent.Create;
  const redirectTo = useAppSuppliedRedirectTo();
  const posthog = usePostHog();

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'magic_link_sent' });
  }, [dispatch]);

  const handleResend = async (email: string) => {
    try {
      if (isCreate) {
        // Sign-up: enroll the new patient and send the magic link.
        await activateSleepioEmail({
          email,
          organizationSlug: state.orgSlug,
          lob: getLob(state.orgSlug),
          phDistinctId: posthog?.get_distinct_id() || undefined,
        });
      } else {
        // Returning-user login: send the magic link WITHOUT re-enrolling.
        // /activate/email re-runs Cortex enrollment, which fails for an
        // already-enrolled patient ("Patient enrollment failed"), so the resend
        // surfaced a generic error and no link arrived. /api/auth/login sends
        // the link with shouldCreateUser: false and no enrollment.
        // (Mirrors EmailEntry's first-send branch, which PR #536 fixed there.)
        await loginUser({
          email,
          product: 'sleepio',
          // A resend has to carry the address too. The first send did, and a member who
          // resends is the same member wanting the same hand-off — dropping it here would
          // mean the second link worked differently from the first.
          redirectTo,
          ph_distinct_id: posthog?.get_distinct_id() || undefined,
        });
      }
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <MagicLinkSent
          imageSrc={SleepioThumbsUp}
          imageAlt="Sleepio Thumbs Up"
          title={t('coverage.pre_onboarding.sleepio.activation.email_sent.title')}
          description={t('coverage.pre_onboarding.sleepio.activation.email_sent.description')}
          cantFindTitle={t('coverage.pre_onboarding.sleepio.activation.email_sent.cant_find_title')}
          cantFindDescription={t('coverage.pre_onboarding.sleepio.activation.email_sent.cant_find_description')}
          email={state.email || ''}
          onResend={handleResend}
          buttonLabel={t('coverage.pre_onboarding.sleepio.activation.email_sent.resend')}
        />
        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

export const EmailSent: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailSentInner />
  </ThemeProvider>
);
